/**
 * Universal Discovery — realtime (B1 / Reverb) contract.
 *
 * This module is the SINGLE place every backend-owned string lives: the Reverb
 * channel name, the broadcast event names, the `discovery.results` payload
 * discriminators, the concurrency error code, and the (still unconfirmed)
 * cancel/rerun endpoints. If the backend contract (AAPU-6017 / AAPU-6031)
 * changes, only this file should need updating.
 *
 * Confirmed contract (on `development`):
 *   channel:  private `universal-list.{universalListId}`  (same channel already used)
 *   events:
 *     - `discovery.progress`  { universalListId, percent }              -> drive the bar
 *     - `discovery.results`   { universalListId, source, count, done }  -> re-fetch the list
 *         source: 'ariadne' done:false -> first wave (~40s): re-fetch, paint fast cards
 *         source: 'merged'  done:true  -> final wave: re-fetch, replace roster, stop
 *   The backend persists each wave BEFORE firing, so handlers only re-fetch —
 *   WS events carry no creator payload and the FE never de-dupes.
 *   `done === true` is ALWAYS the terminal signal, regardless of `source` or how
 *   many events preceded it (feature flag ML_ARIADNE_ENABLED may collapse this to
 *   a single merged/done event — the FE stays flag-agnostic and keys off `done`).
 */

/**
 * Whether Reverb is configured for this deployment (build-time constant). When
 * false the app must behave exactly as before this feature: no live subscription,
 * and no live-only UI (streaming skeletons, the discovery counter, the rotating
 * loading message) — those have no transport to advance or clear them.
 */
export const REVERB_ENABLED = Boolean(process.env.REACT_APP_REVERB_KEY);

/** Private Reverb channel for a single discovery list. */
export const discoveryChannelName = (universalListId) =>
  universalListId != null && universalListId !== '' ? `universal-list.${universalListId}` : null;

/**
 * Broadcast event names in the leading-dot form `useEcho` expects (the leading
 * dot bypasses Laravel's app namespace, matching the Audiences `.generation.progress`
 * convention). Broadcast-as names are `discovery.progress` / `discovery.results`.
 */
export const UD_EVENTS = {
  PROGRESS: '.discovery.progress',
  RESULTS: '.discovery.results',
};

/** `source` discriminator on `discovery.results`. */
export const UD_RESULT_SOURCE = {
  ARIADNE: 'ariadne',
  MERGED: 'merged',
};

/** Denominator for the "Influencer Discovery Counter X/1500" bar. */
export const DISCOVERY_TARGET_DEFAULT = 1500;

/** createV2 rejects a concurrent run with HTTP 409 + this code. */
export const DISCOVERY_IN_FLIGHT_CODE = 'DISCOVERY_IN_FLIGHT';

/**
 * A discovery is already running for this workspace. `arClient.doApiCall` spreads
 * the parsed body onto the callback arg and stamps `httpStatus`, so both the code
 * and the raw status are available here.
 */
export const isDiscoveryInFlight = (resp) =>
  resp?.httpStatus === 409 || resp?.code === DISCOVERY_IN_FLIGHT_CODE;

export const UD_ENDPOINTS = {
  CANCEL: '/universalList/cancelDiscovery',
  RERUN: '/universalList/rerunDiscovery',
};

/** `source` value on a cancel-triggered terminal `discovery.results`. */
export const UD_RESULT_SOURCE_CANCELLED = 'cancelled';

/**
 * Payload adapters — the ONLY code that reads raw WS event field names, so any
 * shape churn is contained to this file. Each tolerates a few candidate keys.
 */
export const readProgressEvent = (e) => ({
  id: String(e?.universalListId ?? e?.universal_list_id ?? e?.id ?? ''),
  percent: e?.percent ?? e?.progress ?? null,
});

export const readResultsEvent = (e) => ({
  id: String(e?.universalListId ?? e?.universal_list_id ?? e?.id ?? ''),
  source: e?.source ?? null,
  count: e?.count ?? null,
  done: Boolean(e?.done),
});
